import React from 'react';
import { motion } from 'framer-motion';
import { Calculator, FileText, Receipt, TrendingUp, Building2, ShieldCheck, ArrowUpRight, MessageCircle } from 'lucide-react';
import { servicesData } from '../../mock';
import { fadeUp, staggerContainer, viewport } from '../../lib/motion';
import { waLink } from '../../lib/whatsapp';

const icons = {
  Calculator, FileText, Receipt, TrendingUp, Building2, ShieldCheck,
};

// Bento spans for asymmetric rhythm
const spans = [
  'md:col-span-7', 'md:col-span-5',
  'md:col-span-5', 'md:col-span-7',
  'md:col-span-7', 'md:col-span-5',
];

export default function Services() {
  return (
    <section id="services" className="relative px-6 lg:px-10 py-28 bg-ink-900">
      <div className="max-w-7xl mx-auto">
        <motion.div
          variants={staggerContainer()}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="max-w-2xl mb-16"
        >
          <motion.span variants={fadeUp} className="text-xs tracking-[0.2em] uppercase font-semibold text-gold-light">Comprehensive Expertise</motion.span>
          <motion.h2 variants={fadeUp} className="font-display text-4xl sm:text-5xl text-white tracking-tight mt-3">Our Professional Services</motion.h2>
          <motion.p variants={fadeUp} className="text-slate-400 mt-4 text-lg leading-relaxed">
            Tailored financial, accounting and tax solutions designed to safeguard your business and accelerate profitable growth.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer(0.1)}
          initial="hidden"
          whileInView="show"
          viewport={viewport}
          className="grid grid-cols-1 md:grid-cols-12 gap-6"
        >
          {servicesData.map((svc, i) => {
            const Icon = icons[svc.icon] || Calculator;
            return (
              <motion.div
                key={svc.id}
                variants={fadeUp}
                whileHover={{ y: -6 }}
                data-testid={`service-card-${svc.id}`}
                className={`group ${spans[i % spans.length]} bg-ink-800 border border-slate-800 hover:border-gold/50 rounded-3xl p-8 transition-colors duration-300 flex flex-col`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-14 h-14 rounded-2xl bg-ink-900 border border-slate-800 flex items-center justify-center text-gold-light group-hover:scale-110 transition-transform">
                    <Icon className="w-7 h-7" strokeWidth={1.25} />
                  </div>
                  <ArrowUpRight className="w-6 h-6 text-slate-600 group-hover:text-gold-light group-hover:rotate-12 transition-all" strokeWidth={1.5} />
                </div>

                <h3 className="font-display text-2xl text-white group-hover:text-gold-light transition-colors">{svc.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mt-3 mb-6">{svc.description}</p>

                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2.5 mb-8 mt-auto border-t border-slate-800 pt-5">
                  {svc.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-xs text-slate-300">
                      <span className="w-1.5 h-1.5 rounded-full bg-gold shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>

                <a
                  href={waLink(`Hello SN Associates, I'm interested in your ${svc.title} service. Please share more details.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`whatsapp-service-${svc.id}`}
                  className="self-start flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink-900 border border-slate-700 hover:bg-[#25D366] hover:text-white hover:border-[#25D366] text-white text-sm font-medium transition-colors"
                >
                  <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> Enquire on WhatsApp
                </a>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
